// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductMiniCard, { ProductMiniCardStyles } from "@guestsupply/App/Components/Product/ProductMiniCard";
import XngCarousel, { SwiperStyle } from "@guestsupply/App/Components/XngCarousel/XngCarousel";
import xngGaGeneralEventEvent from "@guestsupply/App/Handlers/GoogleAnalytics/GeneralEvents/GeneralEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { SectionTitle } from "@guestsupply/App/Widgets/DesignSystem/DesignSystem.helpers";
import { SelectPresentationProps } from "@guestsupply/MobiusOverrides/Select/Select";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@guestsupply/Overrides/Components/ProductCarouselProduct";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { mergeWithCss, xScreen } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { TabPresentationProps } from "@insite/mobius/Tab";
import { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    configuration = "configuration",
    tabs = "tabs",
    featuredProductNumber = "featuredProductNumber",
    featuredItemImageSize = "featuredItemImageSize",
    showImage = "showImage",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    slidesPerView = "slidesPerView",
    isGridView = "isGridView",
    products = "products",
}

interface FeaturedProductsNumbersField {
    fields: {
        productNumber: string;
    };
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.configuration]: string;
        [fields.featuredProductNumber]: string;
        [fields.featuredItemImageSize]: string;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.slidesPerView]: number;
        [fields.isGridView]: boolean;
        [fields.products]: FeaturedProductsNumbersField[];
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    isTabletScreen: state.xCommon.isTabletScreen,
    isMobileScreen: state.xCommon.isMobileScreen,
    // xng-change: add google event
    xGoogleAnalytics: state.xGoogleAnalytics,
});

const mapDispatchToProps = {
    loadProductInfoList,
    // xng-change: add google event
    xngGaGeneralEventEvent,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface FeaturedProductsCarouselStyles {
    container?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    tabGroupStyle?: TabGroupPresentationProps;
    tabStyle?: TabPresentationProps;
    swiperStyles?: SwiperStyle;
    productCategoriesSelect?: SelectPresentationProps;
    carouselProductWrapper?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    productMiniCardStyles?: ProductMiniCardStyles;
    productMiniCardGridViewStyles?: ProductMiniCardStyles;
    productsWrapper?: InjectableCss;
    productWrapper?: InjectableCss;
    productMiniCardWrapper?: InjectableCss;
}

// Xng-Changes: please check FeaturedProductsCarouselExtensions.ts for styles
export const featuredProductsCarouselStyles: FeaturedProductsCarouselStyles = {};

const styles = featuredProductsCarouselStyles;

const FeaturedProductsCarousel = ({
    id,
    products,
    fields,
    errorMessage,
    isTabletScreen,
    isMobileScreen,
    shellContext,
    loadProductInfoList,
    zone,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaGeneralEventEvent,
}: Props) => {
    const dispatch = useDispatch();
    const [sortedProducts, setSortedProducts] = useState<ProductModel[] | undefined>();

    const productIds = useMemo(() => {
        return fields.products?.map(product => {
            return product.fields.productNumber;
        });
    }, [fields.products]);

    useEffect(() => {
        if (fields.configuration === "carouselTabs") {
            if (!productIds?.length || !productIds[0]?.length) {
                return;
            }

            loadProductInfoList({
                id,
                getProductCollectionParameter: {
                    productNumbers: productIds,
                    expand: ["images", "badges", "properties"],
                },
                onComplete: (props: any) => {
                    if (props.products?.length > 0 && zone.includes("tab")) {
                        dispatch({
                            type: "xTabs/EnableZone",
                            data: {
                                zone,
                                isEnabled: props.products.length > 0, // we need to show this tab if we are in tab context
                            },
                        });
                    }
                },
            });
        } else {
            loadProductInfoList({
                id,
                getProductCollectionParameter: {
                    productNumbers: [fields.featuredProductNumber],
                    expand: ["images", "badges", "properties"],
                },
                onComplete: (props: any) => {
                    if (props.products?.length > 0 && zone.includes("tab")) {
                        dispatch({
                            type: "xTabs/EnableZone",
                            data: {
                                zone,
                                isEnabled: props.products.length > 0, // we need to show this tab if we are in tab context
                            },
                        });
                    }
                },
            });
        }
    }, [fields]);

    useLayoutEffect(() => {
        const sortedProducts = products?.sort(
            (product1, product2) =>
                productIds.indexOf(product1.productNumber) - productIds.indexOf(product2.productNumber),
        );
        setSortedProducts(sortedProducts);
    }, [products]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!sortedProducts || sortedProducts?.length === 0) {
        return null;
    }

    if (fields.configuration === "featuredItem") {
        const extendedCarouselProductStyles = mergeWithCss(styles.carouselProductStyles, {
            productImageStyles: {
                linkWrappingImage: {
                    css: css`
                        align-self: center;
                        width: ${fields.featuredItemImageSize}px;
                        height: auto;
                        max-width: 100%;
                    `,
                },
            },
        });

        return (
            <StyledWrapper {...styles.container}>
                {!!fields.title?.length && <SectionTitle>{fields.title}</SectionTitle>}
                <StyledWrapper
                    {...styles.carouselProductWrapper}
                    onClick={() =>
                        xngGaGeneralEventEvent({
                            eventType: GOOGLE_EVENTS.FEATURED_ITEMS,
                            includeEvent: xGoogleAnalytics.includeFeaturedItemsEvent,
                            clickText: sortedProducts?.[0]?.productTitle,
                            destination: sortedProducts?.[0]?.uri,
                        })
                    }
                >
                    <ProductCarouselProduct
                        carouselId={id}
                        product={sortedProducts?.[0]}
                        showImage={fields.showImage}
                        imageSize={fields.featuredItemImageSize}
                        showBrand={false}
                        showTitle={fields.showTitle}
                        showPartNumbers={fields.showPartNumbers}
                        showPrice={fields.showPrice}
                        showAddToCart={fields.showAddToCart}
                        showAddToList={fields.showAddToList}
                        showImageBadges={fields.showImageBadges}
                        maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                        showTextBadges={fields.showTextBadges}
                        maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                        extendedStyles={extendedCarouselProductStyles}
                    />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    if (isTabletScreen) {
        const showNavigation = isMobileScreen ? sortedProducts.length > 1 : sortedProducts.length > 2;
        const ProductsCard = () => (
            <>
                {sortedProducts?.slice(0, 6).map(product => (
                    <StyledWrapper key={product!.id} {...styles.productMiniCardWrapper}>
                        <ProductMiniCard
                            product={product!}
                            widgetId={id}
                            showImage={fields.showImage}
                            showTitle={fields.showTitle}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAvailability={fields.showAvailability}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            showImageBadges={fields.showImageBadges}
                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                            showTextBadges={fields.showTextBadges}
                            maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                            extendedStyles={styles.productMiniCardGridViewStyles}
                            // xng-change: add google event
                            handleClickedLink={(title: string, url: string) =>
                                xngGaGeneralEventEvent({
                                    eventType: GOOGLE_EVENTS.FEATURED_ITEMS,
                                    includeEvent: xGoogleAnalytics.includeFeaturedItemsEvent,
                                    clickText: title,
                                    destination: url,
                                })
                            }
                        />
                    </StyledWrapper>
                ))}
            </>
        );
        return (
            <StyledWrapper {...styles.container}>
                {!!fields.title?.length && <SectionTitle>{fields.title}</SectionTitle>}
                <StyledWrapper>
                    {fields.isGridView ? (
                        <StyledWrapper {...styles.productsWrapper}>
                            <ProductsCard />
                        </StyledWrapper>
                    ) : (
                        <XngCarousel
                            // loop
                            scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                            spaceBetween={22}
                            navigation={showNavigation}
                            draggable={isTouchDevice()}
                            extendedStyles={styles.swiperStyles}
                            breakpoints={{
                                480: {
                                    slidesPerView: 1,
                                },
                                768: {
                                    slidesPerView: 2,
                                    spaceBetween: 20,
                                },
                                1024: {
                                    slidesPerView: fields.slidesPerView,
                                    spaceBetween: 20,
                                },
                            }}
                        >
                            {sortedProducts?.slice(0, 16).map(product => (
                                <ProductMiniCard
                                    key={product!.id}
                                    product={product!}
                                    widgetId={id}
                                    showImage={fields.showImage}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers}
                                    showPrice={fields.showPrice}
                                    showAvailability={fields.showAvailability}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList}
                                    showImageBadges={fields.showImageBadges}
                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                    showTextBadges={fields.showTextBadges}
                                    maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                    extendedStyles={styles.productMiniCardStyles}
                                    // xng-change: add google event
                                    handleClickedLink={(title: string, url: string) =>
                                        xngGaGeneralEventEvent({
                                            eventType: GOOGLE_EVENTS.FEATURED_ITEMS,
                                            includeEvent: xGoogleAnalytics.includeFeaturedItemsEvent,
                                            clickText: title,
                                            destination: url,
                                        })
                                    }
                                />
                            ))}
                        </XngCarousel>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const showNavigation = (isMobileScreen && sortedProducts.length > 1) || sortedProducts.length > 4;
    return (
        <StyledWrapper {...styles.container}>
            {!!fields.title?.length && <SectionTitle>{fields.title}</SectionTitle>}
            {fields.isGridView ? (
                <StyledWrapper {...styles.productsWrapper}>
                    {sortedProducts?.slice(0, 6).map(product => (
                        <StyledWrapper {...styles.productWrapper} key={product!.id}>
                            <ProductMiniCard
                                product={product!}
                                widgetId={id}
                                showImage={fields.showImage}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAvailability={fields.showAvailability}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList}
                                showImageBadges={fields.showImageBadges}
                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                showTextBadges={fields.showTextBadges}
                                maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                extendedStyles={styles.productMiniCardStyles}
                                // xng-change: add google event
                                handleClickedLink={(title: string, url: string) =>
                                    xngGaGeneralEventEvent({
                                        eventType: GOOGLE_EVENTS.FEATURED_ITEMS,
                                        includeEvent: xGoogleAnalytics.includeFeaturedItemsEvent,
                                        clickText: title,
                                        destination: url,
                                    })
                                }
                            />
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            ) : (
                <XngCarousel
                    scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                    spaceBetween={22}
                    navigation={showNavigation}
                    draggable={isTouchDevice()}
                    extendedStyles={styles.swiperStyles}
                    breakpoints={{
                        480: {
                            slidesPerView: 1,
                        },
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 20,
                        },
                        1024: {
                            slidesPerView: fields.slidesPerView,
                            spaceBetween: 20,
                        },
                    }}
                >
                    {sortedProducts?.slice(0, 16).map(product => (
                        <ProductMiniCard
                            key={product!.id}
                            product={product!}
                            widgetId={id}
                            showImage={fields.showImage}
                            showTitle={fields.showTitle}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAvailability={fields.showAvailability}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            showImageBadges={fields.showImageBadges}
                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                            showTextBadges={fields.showTextBadges}
                            maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                            extendedStyles={styles.productMiniCardStyles}
                            // xng-change: add google event
                            handleClickedLink={(title: string, url: string) =>
                                xngGaGeneralEventEvent({
                                    eventType: GOOGLE_EVENTS.FEATURED_ITEMS,
                                    includeEvent: xGoogleAnalytics.includeFeaturedItemsEvent,
                                    clickText: title,
                                    destination: url,
                                })
                            }
                        />
                    ))}
                </XngCarousel>
            )}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const configurationOptions = [
    { value: "carouselTabs", displayName: "Carousel" },
    { value: "featuredItem", displayName: "Featured Item" },
];

const slidesPerViewOptions = [
    { value: 1, displayName: "1" },
    { value: 2, displayName: "2" },
    { value: 3, displayName: "3" },
    { value: 4, displayName: "4" },
    { value: 5, displayName: "5" },
    { value: 6, displayName: "6" },
];

const featuredItemOptions = [
    { value: 150, displayName: "150x150" },
    { value: 300, displayName: "300x300" },
    { value: 500, displayName: "500x500" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(FeaturedProductsCarousel))),
    definition: {
        group: "Xngage Custom" as any,
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.configuration,
                displayName: "Configuration",
                editorTemplate: "DropDownField",
                options: configurationOptions,
                defaultValue: "carouselTabs",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.slidesPerView,
                displayName: "Slides Per View",
                editorTemplate: "DropDownField",
                options: slidesPerViewOptions,
                defaultValue: 4,
                // XNG-CHANGES : GUEST-2728
                isVisible: widget => !widget.fields.isGridView && widget.fields.configuration === "carouselTabs",
                fieldType: "Contextual",
                tab: basicTab,
            },
            // XNG-Changes : GUEST-2720
            {
                name: fields.isGridView,
                displayName: "Grid View",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                // XNG-CHANGES : GUEST-2728
                isVisible: widget => widget.fields.configuration === "carouselTabs",
                fieldType: "General",
            },
            {
                name: fields.products,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                validate: value =>
                    value.length > 16
                        ? translate("You Can Add 16 Products Only, Products more than 16 will not be included")
                        : null,
                getDisplay: item => {
                    return item.fields.productNumber;
                },
                isVisible: widget => widget.fields.configuration === "carouselTabs",
                fieldDefinitions: [
                    {
                        name: "productNumber",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
            {
                name: fields.featuredProductNumber,
                displayName: "Featured Product Number",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Contextual",
                isVisible: widget => widget.fields.configuration === "featuredItem",
                tab: basicTab,
            },
            {
                name: fields.featuredItemImageSize,
                displayName: "Image Size",
                editorTemplate: "DropDownField",
                options: featuredItemOptions,
                defaultValue: 150,
                fieldType: "Contextual",
                isVisible: widget => widget.fields.configuration === "featuredItem",
                tab: basicTab,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
